// Shared layout for the public legal pages (/terms, /privacy, /support).
//
// Pages pass title/updated/intro keys plus an ordered list of section keys;
// every user-visible string comes from the i18n `legal.*` catalog, so the
// wording lives in ru/legal.ts + en/legal.ts, not in components.
//
// These routes sit OUTSIDE AuthGuard (visitors arrive from the login footer
// before they have an account), so the layout renders its own back link and
// the language switcher — mirroring LoginPage's public chrome.

import { useTranslation } from '@govard/i18n-core/react';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Card, Typography } from 'antd';

import { LanguageSwitcher } from '../components/layout/LanguageSwitcher.js';

const { Title, Text, Paragraph } = Typography;

/** One numbered block of the document: a heading plus a paragraph of body text. */
export interface LegalSection {
  titleKey: string;
  bodyKey: string;
}

export interface LegalRelatedLink {
  to: string;
  labelKey: string;
}

export interface LegalPageLayoutProps {
  titleKey: string;
  /** Lead paragraph rendered under the title, before the sections. */
  introKey: string;
  /** Revision label; interpolated with {date} formatted for the active locale. */
  updatedKey: string;
  /** Date of the current legal text revision (page-local constant). */
  revisionDate: Date;
  sections: readonly LegalSection[];
  /** Cross-links to the sibling legal pages shown in the card footer. */
  relatedLinks: readonly LegalRelatedLink[];
}

function formatRevisionDate(date: Date, locale: 'ru' | 'en'): string {
  // Locale-aware formatting per the i18n conventions: no manual concatenation.
  return new Intl.DateTimeFormat(locale === 'ru' ? 'ru-RU' : 'en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

export function LegalPageLayout({
  titleKey,
  introKey,
  updatedKey,
  revisionDate,
  sections,
  relatedLinks,
}: LegalPageLayoutProps): ReactElement {
  const { t, locale } = useTranslation();

  return (
    <main
      className="animate-fade-in flex min-h-screen flex-col items-center p-6"
      style={{ backgroundColor: 'var(--ant-color-bg-layout)' }}
    >
      <div className="flex w-full items-center justify-between" style={{ maxWidth: 720 }}>
        <Link
          to="/dashboard"
          data-testid="legal-back-home"
          className="text-sm transition-colors hover:text-amber-600"
          style={{ color: 'var(--ant-color-text-tertiary)' }}
        >
          {t('crm.legal.back_home')}
        </Link>
        <LanguageSwitcher />
      </div>

      <Card data-testid="legal-page" style={{ width: '100%', maxWidth: 720, marginTop: 16 }}>
        <Title
          level={3}
          style={{
            margin: 0,
            fontSize: 18,
            fontWeight: 600,
            letterSpacing: '-0.015em',
            color: 'var(--ant-color-text)',
          }}
        >
          {t(titleKey)}
        </Title>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t(updatedKey, { date: formatRevisionDate(revisionDate, locale) })}
        </Text>

        <Paragraph style={{ marginTop: 16, color: 'var(--ant-color-text)' }}>
          {t(introKey)}
        </Paragraph>

        <div className="mt-2 flex flex-col gap-6">
          {sections.map((section) => (
            <section key={section.titleKey}>
              <Title
                level={5}
                style={{
                  margin: 0,
                  marginBottom: 4,
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'var(--ant-color-text)',
                }}
              >
                {t(section.titleKey)}
              </Title>
              <Paragraph
                style={{ marginBottom: 0, color: 'var(--ant-color-text-secondary)', fontSize: 13 }}
              >
                {t(section.bodyKey)}
              </Paragraph>
            </section>
          ))}
        </div>

        <div
          className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 border-t pt-4"
          style={{ borderColor: 'var(--ant-color-border)' }}
          data-testid="legal-related-links"
        >
          {relatedLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="text-sm transition-colors hover:text-amber-600"
              style={{ color: 'var(--ant-color-text-tertiary)' }}
            >
              {t(link.labelKey)}
            </Link>
          ))}
        </div>
      </Card>
    </main>
  );
}
